import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock, ArrowLeft } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { formatApiError } from "../../lib/api";
import { EASE } from "../../components/motion";

export default function AdminLogin() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  if (user) {
    navigate("/admin", { replace: true });
    return null;
  }

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      navigate("/admin", { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const googleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + "/admin";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const inputCls = "w-full px-4 py-3 rounded-lg bg-[#F1F5F9] border border-slate-300 text-sm text-slate-700 placeholder:text-slate-600 focus:outline-none focus:border-blue-600/60 transition-colors";

  return (
    <main className="min-h-screen flex items-center justify-center px-5 grid-bg relative" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6, ease: EASE }}
        className="w-full max-w-md bg-panel border border-slate-200 rounded-2xl p-8"
      >
        <div className="flex items-center gap-3 mb-8">
          <span className="w-10 h-10 rounded-lg bg-blue-600/10 border border-blue-600/40 flex items-center justify-center">
            <Lock size={18} className="text-blue-600" />
          </span>
          <div>
            <h1 className="font-heading font-bold text-slate-900">SGS Admin Portal</h1>
            <p className="font-mono text-[10px] uppercase tracking-[0.25em] text-slate-500">Authorized access only</p>
          </div>
        </div>
        <form onSubmit={submit} className="space-y-4">
          <input type="email" required placeholder="Admin email" value={email} onChange={(e) => setEmail(e.target.value)}
            className={inputCls} data-testid="admin-email-input" />
          <input type="password" required placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)}
            className={inputCls} data-testid="admin-password-input" />
          {error && <p className="text-sm text-red-600" data-testid="admin-login-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="admin-login-button"
            className="w-full py-3.5 rounded-full bg-blue-600 text-[#FFFFFF] font-semibold text-sm hover:bg-blue-700 transition-colors disabled:opacity-50">
            {loading ? "Signing in…" : "Sign In"}
          </button>
        </form>
        <div className="flex items-center gap-3 my-5">
          <span className="flex-1 h-px bg-slate-200" />
          <span className="text-[11px] font-mono uppercase tracking-[0.18em] text-slate-400">or</span>
          <span className="flex-1 h-px bg-slate-200" />
        </div>
        <button type="button" onClick={googleLogin} data-testid="google-login-button"
          className="w-full flex items-center justify-center gap-3 py-3.5 rounded-full border border-slate-300 bg-white text-sm font-semibold text-slate-700 hover:border-blue-400 hover:text-blue-600 transition-colors">
          <svg viewBox="0 0 24 24" className="w-5 h-5" aria-hidden="true">
            <path fill="#4285F4" d="M23.49 12.27c0-.79-.07-1.54-.19-2.27H12v4.51h6.47c-.29 1.48-1.14 2.73-2.4 3.58v3h3.86c2.26-2.09 3.56-5.17 3.56-8.82z"/>
            <path fill="#34A853" d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.86-3c-1.08.72-2.45 1.16-4.07 1.16-3.13 0-5.78-2.11-6.73-4.96H1.29v3.09C3.26 21.3 7.31 24 12 24z"/>
            <path fill="#FBBC05" d="M5.27 14.29c-.25-.72-.38-1.49-.38-2.29s.14-1.57.38-2.29V6.62H1.29C.47 8.24 0 10.06 0 12s.47 3.76 1.29 5.38l3.98-3.09z"/>
            <path fill="#EA4335" d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.31 0 3.26 2.7 1.29 6.62l3.98 3.09C6.22 6.86 8.87 4.75 12 4.75z"/>
          </svg>
          Sign in with Google
        </button>
        <Link to="/" data-testid="admin-back-home" className="mt-6 inline-flex items-center gap-2 text-xs text-slate-500 hover:text-blue-600 transition-colors">
          <ArrowLeft size={13} /> Back to website
        </Link>
      </motion.div>
    </main>
  );
}
